/**
 * How the organization's own records are coded — clients, projects, invoices
 * and the rest — as the organization chooses to write them.
 *
 * The server owns every rule: which tokens exist, what makes a pattern sound,
 * which kinds of record this installation has, and what the next code will be.
 * This screen sends what was typed and shows what came back; it works nothing
 * out for itself.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@connect/web-shared';

export const CODE_PATTERNS_ENDPOINT = '/code-patterns';

export type CodeReset = 'never' | 'yearly' | 'halfyearly' | 'monthly' | 'daily';

export interface CodePattern {
  /** The kind of record: `client`, `project`, `invoice`. */
  entity: string;
  label: string;
  description: string;
  pattern: string;
  reset: CodeReset;
  start: number;
  is_default: boolean;
  default_pattern: string;
  default_reset: CodeReset;
  /** The code the next record of this kind gets. */
  next: string;
}

export interface CodePatternDraft {
  pattern: string;
  reset: CodeReset;
  start: number;
}

export interface CodePatternPreview {
  next: string;
  then: string;
}

export const codePatternKeys = {
  all: ['code-patterns'] as const,
  preview: (entity: string, draft: CodePatternDraft) => ['code-patterns', 'preview', entity, draft] as const,
};

export function useCodePatterns() {
  return useQuery({
    queryKey: codePatternKeys.all,
    queryFn: () => api.get<CodePattern[]>(CODE_PATTERNS_ENDPOINT),
  });
}

/** What a pattern would print next, asked of the server as it is typed. */
export function useCodePreview(entity: string, draft: CodePatternDraft, enabled: boolean) {
  return useQuery({
    queryKey: codePatternKeys.preview(entity, draft),
    queryFn: () => api.post<CodePatternPreview>(`${CODE_PATTERNS_ENDPOINT}/${entity}/preview`, draft),
    enabled,
    // A refused pattern is an answer, not a failure to retry.
    retry: false,
    staleTime: 30_000,
  });
}

/** The tokens, as the screen offers them. The server decides what they print. */
export const TOKENS: readonly { token: string; label: string; example: string }[] = [
  { token: '{SEQ:5}', label: 'Running number, 5 digits', example: '00042' },
  { token: '{SEQ}', label: 'Running number', example: '42' },
  { token: '{YEAR}', label: 'Year', example: '2026' },
  { token: '{YY}', label: 'Year, 2 digits', example: '26' },
  { token: '{HALF}', label: 'Half-year, A or B', example: 'B' },
  { token: '{MM}', label: 'Month', example: '10' },
  { token: '{DD}', label: 'Day', example: '10' },
  { token: '{RAND:4}', label: 'Random letters and digits', example: 'K7M2' },
];

export const RESET_OPTIONS: readonly { value: CodeReset; label: string }[] = [
  { value: 'never', label: 'Never — keeps counting' },
  { value: 'yearly', label: 'Every year' },
  { value: 'halfyearly', label: 'Every half-year' },
  { value: 'monthly', label: 'Every month' },
  { value: 'daily', label: 'Every day' },
];
